import React from 'react';
import { i18n } from 'config/locales';
import ValidationElement from 'components/Form/ValidationElement';
import Field from 'components/Form/Field';
import { setAttribute } from 'components/Form/shared/setAttribute';
import { MilitaryDisciplinaryProceduresItemModel } from 'forms/sections/military/disciplinary/types';
import RadioGroup from 'components/Form/RadioGroup';
import Radio from 'components/Form/Radio';
import { ValidationElementProps } from 'components/Form/ValidationElement/ValidationElement';
import { CommonOnError, CommonRequired, CommonScrollIntoView } from 'types/props';
import Textarea from 'components/Form/Textarea';
import DateControl from 'components/Form/DateControl';
import Branch from 'components/Form/Branch';
import NotApplicable from 'components/Form/NotApplicable';
import PVQLocation from 'components/Form/PVQLocation';
import Layouts from 'components/Form/PVQLocation/Layouts';
import PVQName from 'components/Form/PVQName';
import CourtMartialChargeCollection from '../CourtMartialChargeCollection';

interface Props extends ValidationElementProps, Partial<MilitaryDisciplinaryProceduresItemModel> {
  attribute?: string;
  bind?: boolean;
  formType?: string;
  name?: string;
  onError?: CommonOnError;
  onUpdate: (value: Partial<MilitaryDisciplinaryProceduresItemModel>) => void;
  required?: CommonRequired;
  scrollIntoView?: CommonScrollIntoView;
}

export default class DisciplinaryPvq extends ValidationElement<Props> {
  static defaultProps: Props = {
    Comments: {} as MilitaryDisciplinaryProceduresItemModel['Comments'],
    Court: {} as MilitaryDisciplinaryProceduresItemModel['Court'],
    Date: {} as MilitaryDisciplinaryProceduresItemModel['Date'],
    Name: {} as MilitaryDisciplinaryProceduresItemModel['Name'],
    Offenses: {} as MilitaryDisciplinaryProceduresItemModel['Offenses'],
    Outcome: {} as MilitaryDisciplinaryProceduresItemModel['Outcome'],
    // PVQ-only below
    CourtMartialCircumstances: {} as MilitaryDisciplinaryProceduresItemModel['CourtMartialCircumstances'],
    CourtMartialLocation: {} as MilitaryDisciplinaryProceduresItemModel['CourtMartialLocation'],
    CourtMartialOffenseDate: {} as MilitaryDisciplinaryProceduresItemModel['CourtMartialOffenseDate'],
    CourtMartialOffenseLocation: {} as MilitaryDisciplinaryProceduresItemModel['CourtMartialOffenseLocation'],
    CourtMartialType: {} as MilitaryDisciplinaryProceduresItemModel['CourtMartialType'],
    DisciplinaryDate: {} as MilitaryDisciplinaryProceduresItemModel['DisciplinaryDate'],
    DisciplinaryProcedureName: {} as MilitaryDisciplinaryProceduresItemModel['DisciplinaryProcedureName'],
    DisciplinaryProcedureOtherExplanation: {} as MilitaryDisciplinaryProceduresItemModel['DisciplinaryProcedureOtherExplanation'],
    GivenBy: {} as MilitaryDisciplinaryProceduresItemModel['GivenBy'],
    GivenByNotApplicable: { applicable: true },
    Has: {} as MilitaryDisciplinaryProceduresItemModel['Has'],
    InvolvedAlcohol: {} as MilitaryDisciplinaryProceduresItemModel['InvolvedAlcohol'],
    InvolvedDomesticViolence: {} as MilitaryDisciplinaryProceduresItemModel['InvolvedDomesticViolence'],
    InvolvedDrugs: {} as MilitaryDisciplinaryProceduresItemModel['InvolvedDrugs'],
    InvolvedExplosives: {} as MilitaryDisciplinaryProceduresItemModel['InvolvedExplosives'],
    InvolvedFirearms: {} as MilitaryDisciplinaryProceduresItemModel['InvolvedFirearms'],
    List: {} as MilitaryDisciplinaryProceduresItemModel['List'],
    ReportedWithEmployment: {} as MilitaryDisciplinaryProceduresItemModel['ReportedWithEmployment'],
    WhatHappened: {} as MilitaryDisciplinaryProceduresItemModel['WhatHappened'],
    // non-Model properties below
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    onUpdate: (_value: Partial<MilitaryDisciplinaryProceduresItemModel>) => {},
  };

  update = (queue: Partial<MilitaryDisciplinaryProceduresItemModel>) => {
    this.props.onUpdate({
      Comments: this.props.Comments,
      Court: this.props.Court,
      Date: this.props.Date,
      Name: this.props.Name,
      Offenses: this.props.Offenses,
      Outcome: this.props.Outcome,
      // PVQ-only below
      CourtMartialCircumstances: this.props.CourtMartialCircumstances,
      CourtMartialLocation: this.props.CourtMartialLocation,
      CourtMartialOffenseDate: this.props.CourtMartialOffenseDate,
      CourtMartialOffenseLocation: this.props.CourtMartialOffenseLocation,
      CourtMartialType: this.props.CourtMartialType,
      DisciplinaryDate: this.props.DisciplinaryDate,
      DisciplinaryProcedureName: this.props.DisciplinaryProcedureName,
      DisciplinaryProcedureOtherExplanation: this.props.DisciplinaryProcedureOtherExplanation,
      GivenBy: this.props.GivenBy,
      GivenByNotApplicable: this.props.GivenByNotApplicable,
      Has: this.props.Has,
      InvolvedAlcohol: this.props.InvolvedAlcohol,
      InvolvedDomesticViolence: this.props.InvolvedDomesticViolence,
      InvolvedDrugs: this.props.InvolvedDrugs,
      InvolvedExplosives: this.props.InvolvedExplosives,
      InvolvedFirearms: this.props.InvolvedFirearms,
      List: this.props.List,
      ReportedWithEmployment: this.props.ReportedWithEmployment,
      WhatHappened: this.props.WhatHappened,
      ...queue,
    });
  };

  updateCourtMartialCircumstances = (courtMartialCircumstances: MilitaryDisciplinaryProceduresItemModel['CourtMartialCircumstances']) => {
    this.update({
      CourtMartialCircumstances: courtMartialCircumstances,
    });
  };

  updateCourtMartialLocation = (courtMartialLocation: MilitaryDisciplinaryProceduresItemModel['CourtMartialLocation']) => {
    const courtMartialLocationCopy = {
      ...courtMartialLocation,
      isMilitaryDiplomaticAddress: { value: 'Yes' }
    } as MilitaryDisciplinaryProceduresItemModel['CourtMartialLocation'];

    this.update({ CourtMartialLocation: courtMartialLocationCopy });
  };

  updateCourtMartialOffenseDate = (courtMartialOffenseDate: MilitaryDisciplinaryProceduresItemModel['CourtMartialOffenseDate']) => {
    this.update({
      CourtMartialOffenseDate: courtMartialOffenseDate,
    });
  };

  updateCourtMartialOffenseLocation = (courtMartialOffenseLocation: MilitaryDisciplinaryProceduresItemModel['CourtMartialOffenseLocation']) => {
    this.update({
      CourtMartialOffenseLocation: courtMartialOffenseLocation,
    });
  };

  updateCourtMartialType = (courtMartialType: MilitaryDisciplinaryProceduresItemModel['CourtMartialType']) => {
    this.update({
      CourtMartialType: courtMartialType,
    });
  };

  updateDisciplinaryDate = (disciplinaryDate: MilitaryDisciplinaryProceduresItemModel['DisciplinaryDate']) => {
    this.update({
      DisciplinaryDate: disciplinaryDate,
    });
  };

  updateDisciplinaryProcedureName = (disciplinaryProcedureName: MilitaryDisciplinaryProceduresItemModel['DisciplinaryProcedureName']) => {
    const queue: Partial<MilitaryDisciplinaryProceduresItemModel> = { DisciplinaryProcedureName: disciplinaryProcedureName };

    if (disciplinaryProcedureName?.value !== 'CourtMartial') {
      queue.CourtMartialCircumstances = {} as MilitaryDisciplinaryProceduresItemModel['CourtMartialCircumstances'];
      queue.CourtMartialLocation = {} as MilitaryDisciplinaryProceduresItemModel['CourtMartialLocation'];
      queue.CourtMartialOffenseDate = {} as MilitaryDisciplinaryProceduresItemModel['CourtMartialOffenseDate'];
      queue.CourtMartialOffenseLocation = {} as MilitaryDisciplinaryProceduresItemModel['CourtMartialOffenseLocation'];
      queue.CourtMartialType = {} as MilitaryDisciplinaryProceduresItemModel['CourtMartialType'];
      queue.InvolvedAlcohol = {} as MilitaryDisciplinaryProceduresItemModel['InvolvedAlcohol'];
      queue.InvolvedDomesticViolence = {} as MilitaryDisciplinaryProceduresItemModel['InvolvedDomesticViolence'];
      queue.InvolvedDrugs = {} as MilitaryDisciplinaryProceduresItemModel['InvolvedDrugs'];
      queue.InvolvedExplosives = {} as MilitaryDisciplinaryProceduresItemModel['InvolvedExplosives'];
      queue.InvolvedFirearms = {} as MilitaryDisciplinaryProceduresItemModel['InvolvedFirearms'];
      queue.List = {} as MilitaryDisciplinaryProceduresItemModel['List'];
    }

    if (this.props.DisciplinaryProcedureOtherExplanation?.value && disciplinaryProcedureName?.value !== 'Other') {
      queue.DisciplinaryProcedureOtherExplanation = {} as MilitaryDisciplinaryProceduresItemModel['DisciplinaryProcedureName'];
    }

    this.update(queue);
  };

  updateDisciplinaryProcedureOtherExplanation = (disciplinaryProcedureOtherExplanation: MilitaryDisciplinaryProceduresItemModel['DisciplinaryProcedureOtherExplanation']) => {
    this.update({
      DisciplinaryProcedureOtherExplanation: disciplinaryProcedureOtherExplanation,
    });
  };

  updateInvolvedAlcohol = (involvedAlcohol: MilitaryDisciplinaryProceduresItemModel['InvolvedAlcohol']) => {
    this.update({
      InvolvedAlcohol: involvedAlcohol,
    });
  };

  updateInvolvedDomesticViolence = (involvedDomesticViolence: MilitaryDisciplinaryProceduresItemModel['InvolvedDomesticViolence']) => {
    this.update({
      InvolvedDomesticViolence: involvedDomesticViolence,
    });
  };

  updateInvolvedDrugs = (involvedDrugs: MilitaryDisciplinaryProceduresItemModel['InvolvedDrugs']) => {
    this.update({
      InvolvedDrugs: involvedDrugs,
    });
  };

  updateInvolvedExplosives = (involvedExplosives: MilitaryDisciplinaryProceduresItemModel['InvolvedExplosives']) => {
    this.update({
      InvolvedExplosives: involvedExplosives,
    });
  };

  updateInvolvedFirearms = (involvedFirearms: MilitaryDisciplinaryProceduresItemModel['InvolvedFirearms']) => {
    this.update({
      InvolvedFirearms: involvedFirearms,
    });
  };

  updateList = (value: MilitaryDisciplinaryProceduresItemModel['List']) => {
    this.update({
      List: value,
    });
  };

  updateGivenBy = (givenBy: MilitaryDisciplinaryProceduresItemModel['GivenBy']) => {
    this.update({
      GivenBy: givenBy,
    });
  };

  updateGivenByNotApplicable = (givenByNotApplicable: MilitaryDisciplinaryProceduresItemModel['GivenByNotApplicable']) => {
    // Delete the name if it is not applicable because the user selected "I Don't Know"
    const givenBy: MilitaryDisciplinaryProceduresItemModel['GivenBy'] | undefined = givenByNotApplicable?.applicable ? this.props.GivenBy : { } as MilitaryDisciplinaryProceduresItemModel['GivenBy'];

    this.update({
      GivenBy: givenBy,
      GivenByNotApplicable: givenByNotApplicable,
    });
  };

  updateReportedWithEmployment = (reportedWithEmployment: MilitaryDisciplinaryProceduresItemModel['ReportedWithEmployment']) => {
    const queue: Partial<MilitaryDisciplinaryProceduresItemModel> = { ReportedWithEmployment: reportedWithEmployment };

    if (reportedWithEmployment?.value === 'Yes') {
      queue.GivenBy = {} as MilitaryDisciplinaryProceduresItemModel['GivenBy'];
      queue.GivenByNotApplicable = { applicable: true };
      queue.WhatHappened = {} as MilitaryDisciplinaryProceduresItemModel['WhatHappened'];
    }

    this.update(queue);
  };

  updateWhatHappened = (whatHappened: MilitaryDisciplinaryProceduresItemModel['WhatHappened']) => {
    this.update({
      WhatHappened: whatHappened,
    });
  };

  render() {
    return (
      <>
        {/* PVQ: Which disciplinary action did you receive? [...]? */}
        <Field
          scrollIntoView={this.props.scrollIntoView}
          title={i18n.t('military.pvq.disciplinary.heading.disciplinaryprocedurename')}
        >
          <RadioGroup
            className="disciplinary-procedure-name option-list option-list-vertical"
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            onError={this.props.onError}
            required={this.props.required}
            selectedValue={this.props.DisciplinaryProcedureName?.value}
            attribute={setAttribute(this.props.attribute, 'DisciplinaryProcedureName')}
          >
            <Radio
              className="disciplinary-procedure-name-admonishment"
              label={i18n.t('military.pvq.history.label.disciplinary.name.admonishment')}
              name="disciplinary-procedure-name-admonishment"
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
              onError={this.props.onError}
              onUpdate={this.updateDisciplinaryProcedureName}
              value="Admonishment"
            />
            <Radio
              className="disciplinary-procedure-name-reprimand"
              label={i18n.t('military.pvq.history.label.disciplinary.name.reprimand')}
              name="disciplinary-procedure-name-reprimand"
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
              onError={this.props.onError}
              onUpdate={this.updateDisciplinaryProcedureName}
              value="Reprimand"
            />
            <Radio
              className="disciplinary-procedure-name-article15"
              label={i18n.t('military.pvq.history.label.disciplinary.name.article15')}
              name="disciplinary-procedure-name-article15"
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
              onError={this.props.onError}
              onUpdate={this.updateDisciplinaryProcedureName}
              value="Article15"
            />
            <Radio
              className="disciplinary-procedure-name-courtmartial"
              label={i18n.t('military.pvq.history.label.disciplinary.name.courtmartial')}
              name="disciplinary-procedure-name-courtmartial"
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
              onError={this.props.onError}
              onUpdate={this.updateDisciplinaryProcedureName}
              value="CourtMartial"

            />
            <Radio
              className="disciplinary-procedure-name-other"
              label={i18n.t('military.pvq.history.label.disciplinary.name.other')}
              name="disciplinary-procedure-name-other"
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
              onError={this.props.onError}
              onUpdate={this.updateDisciplinaryProcedureName}
              value="Other"
            />
          </RadioGroup>
          {this.props.DisciplinaryProcedureName?.value === 'Other' && (
            <>
              {/* PVQ: Please explain. */}
              <Field
                adjustFor="textarea"
                attribute={setAttribute(this.props.attribute, 'DisciplinaryProcedureOtherExplanation')}
                scrollIntoView={this.props.scrollIntoView}
                title={i18n.t('military.pvq.history.label.disciplinary.explanation')}
              >
                <Textarea
                  // eslint-disable-next-line react/jsx-props-no-spreading
                  {...this.props.DisciplinaryProcedureOtherExplanation}
                  attribute={setAttribute(this.props.attribute, 'DisciplinaryProcedureOtherExplanation')}
                  className="disciplinary-procedure-other-explanation"
                  name="DisciplinaryProcedureOtherExplanation"
                  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                  onError={this.props.onError}
                  onUpdate={this.updateDisciplinaryProcedureOtherExplanation}
                  required={this.props.required}
                />
              </Field>
            </>
          )}
        </Field>
        {/* PVQ: When did you receive this disciplinary action? */}
        {/* new OPM changes no longer skip this question for Court-Martial */}
        {/* (“Other” branch to ZA9.1.7) */}

        <Field
          attribute={setAttribute(this.props.attribute, 'DisciplinaryDate')}
          scrollIntoView={this.props.scrollIntoView}
          title={i18n.t('military.pvq.disciplinary.heading.date')}
        >
          <DateControl
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...this.props.DisciplinaryDate}
            attribute={setAttribute(this.props.attribute, 'DisciplinaryDate')}
            className="disciplinary-date"
            hideDay
            maxDate={{ year: new Date().getFullYear() }}
            minDateEqualTo
            name="DisciplinaryDate"
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            onError={this.props.onError}
            onUpdate={this.updateDisciplinaryDate}
            required={this.props.required}
          />
        </Field>
        {/* PVQ: Did you report this disciplinary action in section [...]? */}
        <Branch
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...this.props.ReportedWithEmployment}
          attribute={setAttribute(this.props.attribute, 'ReportedWithEmployment')}
          className="reported-with-employment"
          label={i18n.t('military.pvq.history.heading.reportedwithemployment')}
          labelSize="h4"
          name="ReportedWithEmployment"
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          onError={this.props.onError}
          onUpdate={this.updateReportedWithEmployment}
          required={this.props.required}
          scrollIntoView={this.props.scrollIntoView}
        />
        {this.props.ReportedWithEmployment?.value === 'No' && (
        <>
          {/* PVQ: Why did you receive this disciplinary action? [...] */}
          <Field
            attribute={setAttribute(this.props.attribute, 'WhatHappened')}
            adjustFor="textarea"
            scrollIntoView={this.props.scrollIntoView}
            title={i18n.t('military.pvq.disciplinary.heading.whathappened')}
          >
            <Textarea
               // eslint-disable-next-line react/jsx-props-no-spreading
              {...this.props.WhatHappened}
              attribute={setAttribute(this.props.attribute, 'WhatHappened')}
              className="what-happened"
              name="WhatHappened"
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
              onError={this.props.onError}
              onUpdate={this.updateWhatHappened}
              required={this.props.required}
            />
          </Field>
          {/* PVQ: Who gave you this disciplinary action? */}
          <Field
            attribute={setAttribute(this.props.attribute, 'GivenBy')}
            scrollIntoView={this.props.scrollIntoView}
            title={i18n.t('military.pvq.disciplinary.heading.givenby')}
          >
            {/* PVQ: I Don't Know */}
            <NotApplicable
                // eslint-disable-next-line react/jsx-props-no-spreading
              {...this.props.GivenByNotApplicable}
              attribute={setAttribute(this.props.attribute, 'GivenByNotApplicable')}
              className="given-by-not-applicable"
              includeNAConfirmation
              label={i18n.t('military.pvq.disciplinary.label.idk')}
              name="GivenByNotApplicable"
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
              onError={this.props.onError}
              onUpdate={this.updateGivenByNotApplicable}
              or={i18n.t('military.pvq.disciplinary.para.or')}
              required={this.props.required}
            >
              <PVQName
                  // eslint-disable-next-line react/jsx-props-no-spreading
                {...this.props.GivenBy}
                attribute={setAttribute(this.props.attribute, 'GivenBy')}
                className="given-by"
                hideMiddleName
                showLastFirstMiddleNameOrder
                name="GivenBy"
                  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                onError={this.props.onError}
                onUpdate={this.updateGivenBy}
                required={this.props.required}
                scrollIntoView={this.props.scrollIntoView}
              />
            </NotApplicable>
          </Field>
        </>
        )}
        {this.props.DisciplinaryProcedureName?.value === `CourtMartial` && (
          <>
            {/* PVQ: What type of court-martial did you have? */}
            <Field
              attribute={setAttribute(this.props.attribute, 'CourtMartialType')}
              scrollIntoView={this.props.scrollIntoView}
              title={i18n.t('military.pvq.disciplinary.heading.courtmartialtype')}
            >
              <RadioGroup
                className="court-martial-type option-list option-list-vertical"
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                onError={this.props.onError}
                required={this.props.required}
                selectedValue={this.props.CourtMartialType?.value}
              >
                <Radio
                  className="court-martial-type-summary"
                  label={i18n.t('military.pvq.history.label.disciplinary.courtmartialtype.summary')}
                  name="court-martial-type-summary"
                  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                  onError={this.props.onError}
                  onUpdate={this.updateCourtMartialType}
                  value="Summary"
                />
                <Radio
                  className="court-martial-type-special"
                  label={i18n.t('military.pvq.history.label.disciplinary.courtmartialtype.special')}
                  name="court-martial-type-special"
                  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                  onError={this.props.onError}
                  onUpdate={this.updateCourtMartialType}
                  value="Special"
                />
                <Radio
                  className="court-martial-type-general"
                  label={i18n.t('military.pvq.history.label.disciplinary.courtmartialtype.general')}
                  name="court-martial-type-general"
                  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                  onError={this.props.onError}
                  onUpdate={this.updateCourtMartialType}
                  value="General"
                />
              </RadioGroup>
            </Field>
            {/* PVQ: Did this court-martial take place in the U.S.? */}
            {/* PVQ: Where did this court-martial take place? */}
            {/* PVQ: City */}
            {/* PVQ: County or County equivalent */}
            {/* PVQ: State or Territory */}
            {/* PVQ: What is the name of the U.S. military installation? */}
            {/* PVQ: Where did this court-martial take place? */}
            {/* PVQ: City */}
            {/* PVQ: Country */}
            {/* PVQ: What is the APO or FPO ZIP Code? */}
            {/* PVQ: What is the name of the U.S. military installation? */}
            <Field
              attribute={setAttribute(this.props.attribute, 'CourtMartialLocation')}
              scrollIntoView={this.props.scrollIntoView}
            >
              <PVQLocation
              // eslint-disable-next-line react/jsx-props-no-spreading
                {...this.props.CourtMartialLocation}
                addressBook="Provider"
                addressBooks={this.props.addressBooks}
                attribute={setAttribute(this.props.attribute, 'CourtMartialLocation')}
                className="court-martial-location"
                countyLabel={i18n.t('military.pvq.disciplinary.label.county')}
                dispatch={this.props.dispatch}
                domesticTitle={i18n.t('military.pvq.disciplinary.heading.courtmartiallocation')}
                foreignTitle={i18n.t('military.pvq.disciplinary.heading.courtmartiallocation')}
                geocode
                includeMilitaryStates
                isPoBoxAllowed={false}
                label={i18n.t('military.pvq.disciplinary.heading.courtmartialinunitedstates')}
                layout={Layouts.PVQ_COURT_MARTIAL_LOCATION}
                militaryDiplomaticInstallationNameLabel={i18n.t('military.pvq.disciplinary.label.usmilitaryinstallationname')}
                name="CourtMartialLocation"
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                onError={this.props.onError}
                onUpdate={this.updateCourtMartialLocation}
                required={this.props.required}
                showInternationalStreet={false}
                zipcodeLabel={i18n.t('military.pvq.disciplinary.label.apofpozipcode')}
              />
            </Field>
            {/* PVQ: What is the date of the offense for which you were [...]? */}
            <Field
              attribute={setAttribute(this.props.attribute, 'CourtMartialOffenseDate')}
              scrollIntoView={this.props.scrollIntoView}
              title={i18n.t('military.pvq.disciplinary.heading.courtmartialoffensedate')}
            >
              <DateControl
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...this.props.CourtMartialOffenseDate}
                attribute={setAttribute(this.props.attribute, 'CourtMartialOffenseDate')}
                className="disciplinary-date"
                hideDay
                maxDate={{ year: new Date().getFullYear() }}
                minDateEqualTo
                name="CourtMartialOffenseDate"
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                onError={this.props.onError}
                onUpdate={this.updateCourtMartialOffenseDate}
                required={this.props.required}
              />
            </Field>
            {/* PVQ: Did this offense occur in the U.S.? */}
            {/* PVQ: Where did it occur? */}
            {/* PVQ: City */}
            {/* PVQ: County or County equivalent */}
            {/* PVQ: State or Territory */}
            {/* PVQ: ZIP Code */}
            {/* PVQ: Is this a U.S. military installation? */}
            {/* PVQ: What is the name of the U.S. military installation? */}
            {/* PVQ: Where did it occur? */}
            {/* PVQ: City */}
            {/* PVQ: Country */}
            {/* PVQ: Is this a U.S. military installation or U.S. diplomatic facility? */}
            {/* PVQ: What is the name of the U.S. military installation or [...]? */}
            {/* PVQ: What is the APO/FPO/DPO ZIP Code for the U.S. military [...]? */}
            <Field
              attribute={setAttribute(this.props.attribute, 'CourtMartialOffenseLocation')}
              scrollIntoView={this.props.scrollIntoView}

            >
              <PVQLocation
              // eslint-disable-next-line react/jsx-props-no-spreading
                {...this.props.CourtMartialOffenseLocation}
                addressBook="Provider"
                addressBooks={this.props.addressBooks}
                attribute={setAttribute(this.props.attribute, 'CourtMartialOffenseLocation')}
                className="court-martial-offense-location"
                countyLabel={i18n.t('military.pvq.disciplinary.label.county')}
                dispatch={this.props.dispatch}
                domesticTitle={i18n.t('military.pvq.disciplinary.heading.courtmartialoffenselocation')}
                foreignTitle={i18n.t('military.pvq.disciplinary.heading.courtmartialoffenselocation')}
                geocode
                includeMilitaryStates
                isPoBoxAllowed={false}
                label={i18n.t('military.pvq.disciplinary.heading.offenseinunitedstates')}
                layout={Layouts.PVQ_COURT_MARTIAL_OFFENSE_LOCATION}
                militaryDiplomaticInstallationNameLabel={i18n.t('military.pvq.disciplinary.label.usmilitaryinstallationname')}
                name="CourtMartialOffenseLocation"
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                onError={this.props.onError}
                onUpdate={this.updateCourtMartialOffenseLocation}
                required={this.props.required}
                showInternationalStreet={false}
                zipcodeLabel={i18n.t('military.pvq.disciplinary.label.zipcode')}
              />
            </Field>
            {/* PVQ: Was domestic violence involved? [...] */}
            <Branch
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...this.props.InvolvedDomesticViolence}
              attribute={setAttribute(this.props.attribute, 'InvolvedDomesticViolence')}
              className="involved-domestic-violence"
              label={i18n.t('military.pvq.disciplinary.heading.involveddomesticviolence')}
              labelSize="h4"
              name="InvolvedDomesticViolence"
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
              onError={this.props.onError}
              onUpdate={this.updateInvolvedDomesticViolence}
              required={this.props.required}
              scrollIntoView={this.props.scrollIntoView}
            />
            {/* PVQ: Were firearms involved? */}
            <Branch
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...this.props.InvolvedFirearms}
              attribute={setAttribute(this.props.attribute, 'InvolvedFirearms')}
              className="involved-firearms"
              label={i18n.t('military.pvq.disciplinary.heading.involvedfirearms')}
              labelSize="h4"
              name="InvolvedFirearms"
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
              onError={this.props.onError}
              onUpdate={this.updateInvolvedFirearms}
              required={this.props.required}
              scrollIntoView={this.props.scrollIntoView}
            />
            {/* PVQ: Were explosives involved? */}
            <Branch
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...this.props.InvolvedExplosives}
              attribute={setAttribute(this.props.attribute, 'InvolvedExplosives')}
              className="involved-explosives"
              label={i18n.t('military.pvq.disciplinary.heading.involvedexplosives')}
              labelSize="h4"
              name="InvolvedExplosives"
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
              onError={this.props.onError}
              onUpdate={this.updateInvolvedExplosives}
              required={this.props.required}
              scrollIntoView={this.props.scrollIntoView}
            />
            {/* PVQ: Were drugs involved? */}
            <Branch
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...this.props.InvolvedDrugs}
              attribute={setAttribute(this.props.attribute, 'InvolvedDrugs')}
              className="involved-drugs"
              label={i18n.t('military.pvq.disciplinary.heading.involveddrugs')}
              labelSize="h4"
              name="InvolvedDrugs"
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
              onError={this.props.onError}
              onUpdate={this.updateInvolvedDrugs}
              required={this.props.required}
              scrollIntoView={this.props.scrollIntoView}
            />
            {/* PVQ: Was alcohol involved? */}
            <Branch
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...this.props.InvolvedAlcohol}
              attribute={setAttribute(this.props.attribute, 'InvolvedAlcohol')}
              className="involved-alcohol"
              label={i18n.t('military.pvq.disciplinary.heading.involvedalcohol')}
              labelSize="h4"
              name="InvolvedAlcohol"
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
              onError={this.props.onError}
              onUpdate={this.updateInvolvedAlcohol}
              required={this.props.required}
              scrollIntoView={this.props.scrollIntoView}
            />
            {/* PVQ: What were the circumstances surrounding this offense? */}
            <Field
              adjustFor="textarea"
              attribute={setAttribute(this.props.attribute, 'CourtMartialCircumstances')}
              scrollIntoView={this.props.scrollIntoView}
              title={i18n.t('military.pvq.disciplinary.label.courtmartialcircumstances')}
            >
              <Textarea
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...this.props.CourtMartialCircumstances}
                attribute={setAttribute(this.props.attribute, 'CourtMartialCircumstances')}
                className="court-martial-circumstances"
                name="CourtMartialCircumstances"
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                onError={this.props.onError}
                onUpdate={this.updateCourtMartialCircumstances}
                required={this.props.required}
              />
            </Field>
            {/* PVQ: What were your charges under the Uniform Code of [...]? */}
            {/* PVQ: What was the outcome for this charge? */}
            {/* PVQ: Please explain. */}
            {/* PVQ: What were the consequences of this disciplinary action? [...] */}
            {/* PVQ: Please explain. */}
            {/* PVQ: Do you have another charge for this offense to report? */}
            <CourtMartialChargeCollection
              {...this.props.List}
              attribute={setAttribute(this.props.attribute, 'List')}
              formType={this.props.formType}
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
              onError={this.props.onError}
              onUpdate={this.updateList}
              required={this.props.required}
            />
          </>
        )}
      </>
    );
  }
}
